import { useForm } from "react-hook-form";
import * as yup from "yup";
import { InputFormField } from "../common/formfields/InputFormField";
import { FormButtons } from "../common/forms/FormButtons";
import { FormHeader } from "../common/forms/FormHeader";
import styles from "../common/forms/forms.module.css";
import { Project, ProjectFilters, ProjectFieldLabels, projectsConstants } from "../api/Projects";
import { useProjectPersonPermissionsEditForm } from "../ProjectPersonPermissions/ProjectPersonPermissionsEditForm";
import { ProjectPersonPermission } from "../ProjectPersonPermissions/ProjectPersonPermissions";
import { CustomTypedEntityFormProps, FormEventTypes } from "../common/entity/EntityInterfaces";
import { IResultModel } from "../api/GenericTypes";
import { useContext } from "react";
import { SessionContext } from "../common/contexts/SessionContext";
import {
  EntityCustomTypeForm,
  useCustomTypeForm,
  useCustomTypeResolver,
} from "../Customization/CustomTypes/generics/useCustomTypeForm";
import { calculateDirtyFields } from "../common/forms/utils/FormUtils";

export const useProjectFormSchema = (initialValues?: Partial<Project>, event?: FormEventTypes) => {
  const { api } = useContext(SessionContext);
  const ProjectFormSchema = {
    name: yup
      .string()
      .required("Project name is required")
      .typeError("Project name is required")
      .test({
        name: "NoWhitespaces",
        test: (value) => value === value?.trim(),
        message: "Whitespaces at the beginning or at the end of the name are not allowed",
      })
      .test({
        name: "UniqueProjects",
        test: async (value) => {
          if (!value) return false;
          if (value === initialValues?.name && event !== "CLONE") return true;
          const existingProjectIds = (
            (await api.post("projects/ids_only", { names: [value] } as ProjectFilters, {})) as IResultModel<number>
          )?.results;
          return !!existingProjectIds && !existingProjectIds.length;
        },
        message: "Project name already exists",
      }),
    projectPersonPermissions: yup
      .array()
      .typeError("Invalid input for field 'Project access'")
      .min(1, "A project requires at least one administrator")
      .test({
        test: (value) => !!(value as ProjectPersonPermission[]).filter((p) => p.administer).length,
        message: "A project requires at least one administrator",
      }),
  };
  return ProjectFormSchema;
};

export const ProjectForm = ({
  title,
  subtitle,
  initialValues,
  onSubmit,
  onCancel,
  loading,
  submitButtonLabel,
  event,
  typeId,
  typesOverride,
}: CustomTypedEntityFormProps<"projects">) => {
  const ProjectFormSchema = useProjectFormSchema(initialValues, event);
  const { typedResolver } = useCustomTypeResolver<Partial<Project>>(ProjectFormSchema);

  // const { defaultValues, typedFormSchema, processCustomFields, type, setType, types } = useCustomTypeForm({
  //   initialValues: initialValues,
  //   formSchema: ProjectFormSchema,
  //   typeId: typeId,
  //   entityType: "Project",
  // });

  const {
    reset,
    getValues,
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Partial<Project>>({
    defaultValues: initialValues,
    resolver: typedResolver,
  });
  const { processCustomFields, types } = useCustomTypeForm({
    control,
    reset,
    getValues,
    entityType: "Project",
    typeId,
    typesOverride,
  });

  const { form: projectPersonPermissionsEditForm, reset: handleProjectPersonPermissionReset } =
    useProjectPersonPermissionsEditForm({
      id: "projectPersonPermissions",
      errors: errors,
      project: initialValues as Project,
      getProjectPersonPermissions: (values) => {
        setValue("projectPersonPermissions", values);
      },
    });

  return (
    <>
      <FormHeader title={title} subtitle={subtitle} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        autoComplete="off"
        className={`form-horizontal ${styles.form_holder}`}
      >
        <fieldset>
          <legend className="col-md-offset-2 col-md-10">Details</legend>
        </fieldset>

        <InputFormField
          id="name"
          label={ProjectFieldLabels.name}
          errors={errors}
          register={register}
          autoFocus={true}
          required={true}
          placeholder="Enter project name..."
        />

        <fieldset>
          <legend className="col-md-offset-2 col-md-10">
            Project access<span style={{ color: "var(--danger)" }}>*</span>
          </legend>
        </fieldset>

        {projectPersonPermissionsEditForm}

        <EntityCustomTypeForm
          entityType="Project"
          types={types}
          control={control}
          setValue={setValue}
          initialValues={initialValues}
          errors={errors}
          entityConstants={projectsConstants}
        />

        <FormButtons
          onClose={onCancel}
          onSubmit={handleSubmit(async (entity) => await onSubmit(processCustomFields(entity)))}
          submitButtonLabel={submitButtonLabel || "Save changes"}
          disabled={isSubmitting}
          errors={errors}
          loading={loading}
          reset={
            ["EDIT", "CLONE"].includes(event)
              ? () => {
                  reset(initialValues);
                  handleProjectPersonPermissionReset();
                }
              : undefined
          }
          dirtyFields={["EDIT", "CLONE"].includes(event) ? calculateDirtyFields(dirtyFields) : undefined}
        />
      </form>
    </>
  );
};
